import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsersCog, faPlus, faKey, faBan, faCheckCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPER_ADMIN_NAV } from './nav';

interface InternalUserRow {
    id: number; full_name: string; email: string; department: string; department_label: string;
    role: string; role_label: string; active: boolean; phone: string | null; last_login_at: string | null;
}

interface Grant {
    id: number; principal_type: string; principal_id: string; dashboard_id: string; dashboard_label: string;
    is_internal: boolean; granted_at: string; is_active: boolean; revoked_at: string | null;
}

const DEPARTMENTS = ['support', 'finance', 'compliance', 'customer_relations', 'business_development', 'people', 'engineering', 'executive'];
const ROLES = ['agent', 'specialist', 'manager', 'director', 'super_admin'];
const PRINCIPAL_TYPES = ['patient', 'staff', 'institution', 'provider', 'internal_user'];
const DASHBOARD_IDS = [
    'general_provider', 'clinician', 'facility', 'institution', 'patient', 'individual_provider',
    'support_center', 'finance_center', 'compliance_center', 'customer_relations', 'business_development', 'human_resources', 'super_admin',
];

const UserGrantAdmin: React.FC = () => {
    const { api } = useInternalApi();
    const [tab, setTab] = useState<'users' | 'grants'>('users');

    const [users, setUsers] = useState<InternalUserRow[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', department: 'support', role: 'agent' });
    const [submitting, setSubmitting] = useState(false);
    const [editing, setEditing] = useState<InternalUserRow | null>(null);

    const [grants, setGrants] = useState<Grant[]>([]);
    const [loadingGrants, setLoadingGrants] = useState(false);
    const [grantForm, setGrantForm] = useState({ principal_type: 'internal_user', principal_id: '', dashboard_id: 'super_admin' });
    const [grantFilter, setGrantFilter] = useState({ principal_type: '', principal_id: '' });

    useEffect(() => { document.title = 'User & Grant Administration - Uzima Internal'; }, []);

    const loadUsers = async () => {
        setLoadingUsers(true);
        try {
            const res = await api.get('platform/admin/internal-users/');
            if (res.data?.status === 'success') setUsers(res.data.data || []);
        } catch { setUsers([]); } finally { setLoadingUsers(false); }
    };

    const loadGrants = async () => {
        setLoadingGrants(true);
        try {
            const params = new URLSearchParams();
            if (grantFilter.principal_type) params.set('principal_type', grantFilter.principal_type);
            if (grantFilter.principal_id) params.set('principal_id', grantFilter.principal_id);
            const res = await api.get(`platform/admin/grants/?${params}`);
            if (res.data?.status === 'success') setGrants(res.data.data || []);
        } catch { setGrants([]); } finally { setLoadingGrants(false); }
    };

    useEffect(() => { loadUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (tab === 'grants') loadGrants(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitCreate = async () => {
        if (!form.first_name || !form.last_name || !form.email || !form.password) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'First name, last name, email and password are required.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post('platform/admin/internal-users/', form);
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Internal User Created', timer: 1500, showConfirmButton: false });
                setShowCreate(false);
                setForm({ first_name: '', last_name: '', email: '', password: '', department: 'support', role: 'agent' });
                loadUsers();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to create user.' });
        } finally {
            setSubmitting(false);
        }
    };

    const toggleActive = async (u: InternalUserRow) => {
        try {
            await api.patch(`platform/admin/internal-users/${u.id}/`, { active: !u.active });
            loadUsers();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update user.' });
        }
    };

    const saveEdit = async () => {
        if (!editing) return;
        try {
            await api.patch(`platform/admin/internal-users/${editing.id}/`, { department: editing.department, role: editing.role });
            setEditing(null);
            loadUsers();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not save changes.' });
        }
    };

    const resetPassword = async (u: InternalUserRow) => {
        const { value: password } = await Swal.fire({
            title: `Reset password for ${u.full_name}`, input: 'password', inputLabel: 'New password',
            showCancelButton: true, inputValidator: (v) => (!v || v.length < 8 ? 'At least 8 characters' : undefined),
        });
        if (!password) return;
        try {
            await api.patch(`platform/admin/internal-users/${u.id}/`, { password });
            Swal.fire({ icon: 'success', title: 'Password reset', timer: 1200, showConfirmButton: false });
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not reset password.' });
        }
    };

    const submitGrant = async () => {
        if (!grantForm.principal_id) {
            Swal.fire({ icon: 'warning', title: 'Missing principal_id', text: 'Enter the principal ID to grant access to.' });
            return;
        }
        try {
            const res = await api.post('platform/admin/grants/', { ...grantForm, action: 'grant' });
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Access granted', timer: 1200, showConfirmButton: false });
                loadGrants();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not grant access.' });
        }
    };

    const revokeGrant = async (g: Grant) => {
        const confirm = await Swal.fire({ title: 'Revoke this dashboard access?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#f06548' });
        if (!confirm.isConfirmed) return;
        try {
            await api.post('platform/admin/grants/', { principal_type: g.principal_type, principal_id: g.principal_id, dashboard_id: g.dashboard_id, action: 'revoke' });
            loadGrants();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not revoke access.' });
        }
    };

    return (
        <InternalShell activeDashboard="super_admin" subNav={SUPER_ADMIN_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faUsersCog} className="me-2 text-primary" />User & Grant Administration</h2>
                    <p className="text-muted mb-0">Internal staff accounts and dashboard access grants for every principal on the platform</p>
                </div>
                {tab === 'users' && (
                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />New Internal User</button>
                )}
            </div>

            <ul className="nav nav-pills mb-4">
                <li className="nav-item"><button className={`nav-link ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>Internal Users</button></li>
                <li className="nav-item"><button className={`nav-link ${tab === 'grants' ? 'active' : ''}`} onClick={() => setTab('grants')}>Dashboard Grants</button></li>
            </ul>

            {tab === 'users' && (
                <div className="card border-0 shadow-sm">
                    <div className="card-body p-0">
                        {loadingUsers ? (
                            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                        ) : users.length === 0 ? (
                            <div className="text-center py-5 text-muted">No internal users yet.</div>
                        ) : (
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light"><tr><th>Name</th><th>Email</th><th>Department</th><th>Role</th><th>Last Login</th><th>Status</th><th>Actions</th></tr></thead>
                                <tbody>
                                    {users.map(u => (
                                        <tr key={u.id}>
                                            <td>{u.full_name}</td>
                                            <td>{u.email}</td>
                                            <td>
                                                {editing?.id === u.id ? (
                                                    <select className="form-select form-select-sm" value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })}>
                                                        {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                                                    </select>
                                                ) : u.department_label}
                                            </td>
                                            <td>
                                                {editing?.id === u.id ? (
                                                    <select className="form-select form-select-sm" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                                                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                                    </select>
                                                ) : u.role_label}
                                            </td>
                                            <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}</td>
                                            <td><span className={`badge ${u.active ? 'bg-success' : 'bg-secondary'}`}>{u.active ? 'Active' : 'Disabled'}</span></td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {editing?.id === u.id ? (
                                                        <>
                                                            <button className="btn btn-sm btn-success" onClick={saveEdit}><FontAwesomeIcon icon={faCheckCircle} /></button>
                                                            <button className="btn btn-sm btn-light" onClick={() => setEditing(null)}><FontAwesomeIcon icon={faTimes} /></button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(u)}>Edit</button>
                                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => resetPassword(u)}><FontAwesomeIcon icon={faKey} /></button>
                                                            <button className={`btn btn-sm ${u.active ? 'btn-outline-danger' : 'btn-outline-success'}`} onClick={() => toggleActive(u)}>
                                                                <FontAwesomeIcon icon={u.active ? faBan : faCheckCircle} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {tab === 'grants' && (
                <div className="row">
                    <div className="col-lg-4 mb-4">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white"><strong>Grant Dashboard Access</strong></div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">Principal Type</label>
                                    <select className="form-select" value={grantForm.principal_type} onChange={(e) => setGrantForm(f => ({ ...f, principal_type: e.target.value }))}>
                                        {PRINCIPAL_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Principal ID</label>
                                    <input className="form-control" placeholder="e.g. 5 or a patient_id" value={grantForm.principal_id} onChange={(e) => setGrantForm(f => ({ ...f, principal_id: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Dashboard</label>
                                    <select className="form-select" value={grantForm.dashboard_id} onChange={(e) => setGrantForm(f => ({ ...f, dashboard_id: e.target.value }))}>
                                        {DASHBOARD_IDS.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>
                                <button className="btn btn-primary w-100" onClick={submitGrant}>Grant Access</button>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-8 mb-4">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <strong>Recent Grants</strong>
                                <div className="d-flex gap-2">
                                    <input className="form-control form-control-sm" placeholder="Filter principal ID" style={{ width: 160 }}
                                        value={grantFilter.principal_id} onChange={(e) => setGrantFilter(f => ({ ...f, principal_id: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && loadGrants()} />
                                    <button className="btn btn-sm btn-outline-primary" onClick={loadGrants}>Filter</button>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                {loadingGrants ? (
                                    <div className="text-center py-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                                ) : grants.length === 0 ? (
                                    <div className="text-center py-4 text-muted">No grants found.</div>
                                ) : (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light"><tr><th>Principal</th><th>Dashboard</th><th>Granted</th><th>Status</th><th></th></tr></thead>
                                        <tbody>
                                            {grants.map(g => (
                                                <tr key={g.id}>
                                                    <td>{g.principal_type}:{g.principal_id}</td>
                                                    <td>{g.dashboard_label} {g.is_internal && <span className="badge bg-secondary ms-1">internal</span>}</td>
                                                    <td>{new Date(g.granted_at).toLocaleDateString()}</td>
                                                    <td><span className={`badge ${g.is_active ? 'bg-success' : 'bg-secondary'}`}>{g.is_active ? 'Active' : 'Revoked'}</span></td>
                                                    <td>{g.is_active && <button className="btn btn-sm btn-outline-danger" onClick={() => revokeGrant(g)}>Revoke</button>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Internal User</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">First Name</label>
                                        <input className="form-control" value={form.first_name} onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Last Name</label>
                                        <input className="form-control" value={form.last_name} onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Email</label>
                                    <input type="email" className="form-control" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Password</label>
                                    <input type="password" className="form-control" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Department</label>
                                        <select className="form-select" value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))}>
                                            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                                        </select>
                                        <div className="form-text">Grants that department's default dashboards on creation.</div>
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Role</label>
                                        <select className="form-select" value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}>
                                            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitCreate}>{submitting ? 'Creating...' : 'Create'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default UserGrantAdmin;
